// AladinnHomePage.js - Version mobile optimisée avec parcours prioritaire et assistance contextuelle
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { collection, query, orderBy, getDocs, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Target, Search, BookOpen, Zap, Settings, Plus, ArrowRight,
  Lightbulb, GraduationCap, Compass, Clock, ChevronLeft,
  ChevronRight, List, AlertCircle, Menu, X, Star, TrendingUp,
  Users, Heart, CheckCircle2, PlayCircle, HelpCircle, Info,
  Sparkles, Eye
} from 'lucide-react';

// Import des modules existants
import ObjectiveModule from './ObjectiveModule';
import StrategyActionModule from './StrategyActionModule';
import InspirationModule from './InspirationModule';
import ExplorationModule from './ExplorationModule';

// IMPORT du système d'aide contextuelle dédié
import { HelpButton, WelcomeBanner, GuidedTour } from './ContextualHelpSystem';
import userPreferencesManager from '../components/UserPreferencesManager';

// ============================================================================
// MAPPING DES COULEURS POUR TAILWIND
// ============================================================================
// Tailwind ne peut pas générer dynamiquement des classes avec template literals
// Il faut utiliser des classes complètes pour qu'elles soient incluses dans le CSS final
const STEP_COLOR_CLASSES = {
  purple: {
    bg500: 'bg-purple-500',
    bg400: 'bg-purple-400',
    bg100: 'bg-purple-100',
    bg50: 'bg-purple-50',
    bg200: 'bg-purple-200',
    border200: 'border-purple-200',
    border300: 'border-purple-300',
    text700: 'text-purple-700',
    text500: 'text-purple-500'
  },
  blue: {
    bg500: 'bg-blue-500',
    bg400: 'bg-blue-400',
    bg100: 'bg-blue-100',
    bg50: 'bg-blue-50',
    bg200: 'bg-blue-200',
    border200: 'border-blue-200',
    border300: 'border-blue-300',
    text700: 'text-blue-700',
    text500: 'text-blue-500'
  },
  orange: {
    bg500: 'bg-orange-500',
    bg400: 'bg-orange-400',
    bg100: 'bg-orange-100',
    bg50: 'bg-orange-50',
    bg200: 'bg-orange-200',
    border200: 'border-orange-200',
    border300: 'border-orange-300',
    text700: 'text-orange-700',
    text500: 'text-orange-500'
  },
  green: {
    bg500: 'bg-green-500',
    bg400: 'bg-green-400',
    bg100: 'bg-green-100',
    bg50: 'bg-green-50',
    bg200: 'bg-green-200',
    border200: 'border-green-200',
    border300: 'border-green-300',
    text700: 'text-green-700',
    text500: 'text-green-500'
  },
  teal: {
    bg500: 'bg-teal-500',
    bg400: 'bg-teal-400',
    bg100: 'bg-teal-100',
    bg50: 'bg-teal-50',
    bg200: 'bg-teal-200',
    border200: 'border-teal-200',
    border300: 'border-teal-300',
    text700: 'text-teal-700',
    text500: 'text-teal-500'
  }
};

// ============================================================================
// COMPOSANT DE NAVIGATION DU PARCOURS - VERSION COMPACTE (ICÔNES UNIQUEMENT)
// ============================================================================

const JourneyNavigationCompact = React.memo(({
  currentStep,
  onStepChange,
  currentCompetence,
  journeySteps,
  isMobile,
  onToggleCollapse
}) => {
  return (
    <div className="w-full h-full p-2 overflow-y-auto flex flex-col items-center">
      {/* Bouton pour déplier */}
      <button
        onClick={onToggleCollapse}
        className="w-full p-3 mb-4 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors group relative"
        title="Déplier le panneau"
      >
        <ChevronRight className="w-6 h-6 mx-auto" />

        {/* Tooltip */}
        <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg">
          <div className="font-medium">Déplier le panneau</div>
          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
        </div>
      </button>

      {/* Étapes du parcours - Version compacte */}
      <div className="space-y-2 w-full">
        {journeySteps.map((step) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isCompleted = step.completed;
          const isAccessible = step.id === 'ambition' ||
            (currentCompetence && ['strategy', 'action', 'inspiration', 'exploration'].includes(step.id));

          return (
            <button
              key={step.id}
              onClick={() => {
                if (isAccessible) {
                  onStepChange(step.id);
                }
              }}
              disabled={!isAccessible}
              className={`w-full p-3 rounded-lg transition-all group relative ${STEP_COLOR_CLASSES[step.color].bg500} ${isActive
                ? 'shadow-lg ring-2 ring-white ring-offset-2 opacity-100'
                : 'opacity-80 hover:opacity-100'
                } ${!isAccessible ? 'cursor-not-allowed opacity-50' : ''}`}
              title={step.title}
            >
              <Icon className="w-6 h-6 mx-auto text-white" />

              {/* Tooltip au survol */}
              <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg">
                <div className="font-medium">{step.title}</div>
                <div className="text-xs text-gray-300">{step.description}</div>
                {/* Flèche du tooltip */}
                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Indicateur d'objectif actuel - Version compacte */}
      {currentCompetence && (
        <div className="mt-4 p-2 bg-blue-100 rounded-lg w-full group relative">
          <Target className="w-5 h-5 text-blue-600 mx-auto" />

          {/* Tooltip objectif */}
          <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm w-48 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg">
            <div className="font-medium text-xs mb-1">Objectif actuel</div>
            <div className="text-xs text-gray-300 line-clamp-3">{currentCompetence.title}</div>
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
});

JourneyNavigationCompact.displayName = 'JourneyNavigationCompact';

// ============================================================================
// COMPOSANT DE NAVIGATION DU PARCOURS - VERSION MOBILE PRIORITAIRE AVEC ASSISTANCE
// ============================================================================

const JourneyNavigation = React.memo(({
  currentStep,
  onStepChange,
  currentCompetence,
  journeySteps,
  onCreateObjective,
  onListObjectives,
  userObjectives,
  navigate,
  isMobile,
  onClose,
  onShowGuide,
  isCollapsed,
  onToggleCollapse
}) => {
  // Si collapsed sur desktop, afficher la version compacte
  if (isCollapsed && !isMobile) {
    return <JourneyNavigationCompact
      currentStep={currentStep}
      onStepChange={onStepChange}
      currentCompetence={currentCompetence}
      journeySteps={journeySteps}
      isMobile={isMobile}
      onToggleCollapse={onToggleCollapse}
    />;
  }

  return (
    <div className="w-full h-full p-4 lg:p-6 overflow-y-auto">


      {/* Header desktop avec aide contextuelle et bouton collapse */}
      {!isMobile && (
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Votre parcours</h3>
              <p className="text-sm text-gray-600">Etape par étape</p>
            </div>
            <button
              onClick={onToggleCollapse}
              className="ml-2 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              title="Réduire le panneau"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Header mobile simple */}
      {isMobile && (
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Votre parcours</h3>
              <p className="text-sm text-gray-600">Aladinn vous guide étape par étape</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3 lg:space-y-4">
        {journeySteps.map((step, index) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isCompleted = step.completed;
          const isAccessible = step.id === 'ambition' ||
            (currentCompetence && ['strategy', 'action', 'inspiration', 'exploration'].includes(step.id));

          return (
            <div
              key={step.id}
              className={`p-3 lg:p-4 rounded-lg transition-all cursor-pointer ${STEP_COLOR_CLASSES[step.color].bg100} border ${isActive
                ? `border-2 ${STEP_COLOR_CLASSES[step.color].border300} opacity-100 shadow-xl scale-[1.02]`
                : `${STEP_COLOR_CLASSES[step.color].border200} opacity-80 hover:opacity-100 hover:shadow-md`
                } ${!isAccessible ? 'cursor-not-allowed opacity-50' : ''}`}
              onClick={() => {
                if (isAccessible) {
                  onStepChange(step.id);
                  if (isMobile) onClose();
                }
              }}
            >
              <div className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${STEP_COLOR_CLASSES[step.color].bg500} ${isActive ? 'ring-2 ring-white shadow-md' : ''
                  }`}>
                  {isCompleted && !isActive ? (
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  ) : (
                    <Icon className="w-4 h-4 text-white" />
                  )}
                </div>

                <div className="flex-grow min-w-0">
                  <h4 className={`font-medium truncate ${isActive ? STEP_COLOR_CLASSES[step.color].text700 : 'text-gray-700'
                    }`}>
                    {step.title}
                  </h4>
                  <p className="text-xs text-gray-500 truncate">{step.description}</p>
                </div>

                <div className="flex items-center space-x-2">
                  {/* Aide contextuelle uniquement sur desktop */}
                  {!isMobile && isActive && (
                    <HelpButton
                      stepId={step.id}
                      isActive={true}
                      size="sm"
                      title="Aide pour cette étape"
                    />
                  )}
                  {isActive && (
                    <ArrowRight className={`w-4 h-4 ${STEP_COLOR_CLASSES[step.color].text500} flex-shrink-0`} />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Informations sur l'objectif actuel */}
      {currentCompetence && (
        <div className="mt-6 lg:mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-blue-800 mb-2">Objectif actuel</h4>
          <p className="text-sm text-blue-700 break-words line-clamp-3">{currentCompetence.title}</p>
          {currentCompetence.theme && (
            <span className="inline-block mt-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full truncate max-w-full">
              {currentCompetence.theme}
            </span>
          )}
        </div>
      )}

      {/* Actions rapides */}
      <div className="mt-6 lg:mt-8 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-700 mb-3">Actions rapides</h4>
        <div className="space-y-2">
          <button
            onClick={() => {
              navigate('/progression');
              if (isMobile) onClose();
            }}
            className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-white rounded-lg transition-colors flex items-center gap-2"
          >
            <Target className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">Tableau de bord</span>
          </button>
          <button
            onClick={() => {
              onStepChange('inspiration');
              if (isMobile) onClose();
            }}
            className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-white rounded-lg transition-colors flex items-center gap-2"
          >
            <Search className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">Trouver l'inspiration</span>
          </button>
          <button
            onClick={() => {
              onStepChange('exploration');
              if (isMobile) onClose();
            }}
            className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-white rounded-lg transition-colors flex items-center gap-2"
          >
            <BookOpen className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">Explorer les connaissances</span>
          </button>
        </div>
      </div>
    </div>
  );
});

JourneyNavigation.displayName = 'JourneyNavigation';

// ============================================================================
// COMPOSANT DE PRÉSENTATION DU PARCOURS - VERSION MOBILE UNIQUEMENT
// ============================================================================

const JourneyPresentation = React.memo(({
  journeySteps,
  currentStep,
  onStepChange,
  currentCompetence,
  isMobile
}) => {
  return (
    <div className="mb-6">
      {/* Titre section mobile */}
      {isMobile && (
        <div className="mb-4">
          <h2 className="text-xl font-bold text-gray-800 mb-2">Votre Parcours Aladinn</h2>
          <p className="text-sm text-gray-600">Suivez votre progression étape par étape</p>
        </div>
      )}

      {/* Affichage mobile uniquement : carte par carte */}
      {isMobile && (
        <div className="space-y-3">
          {journeySteps.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = step.completed;
            const isAccessible = step.id === 'ambition' ||
              (currentCompetence && ['strategy', 'action', 'inspiration', 'exploration'].includes(step.id));

            return (
              <div
                key={step.id}
                className={`p-4 rounded-xl transition-all cursor-pointer ${STEP_COLOR_CLASSES[step.color].bg100} border ${isActive
                  ? `border-2 ${STEP_COLOR_CLASSES[step.color].border300} opacity-100 shadow-xl scale-[1.02]`
                  : `${STEP_COLOR_CLASSES[step.color].border200} opacity-80 hover:opacity-100 hover:shadow-md`
                  } ${!isAccessible ? 'cursor-not-allowed opacity-50' : ''}`}
                onClick={() => {
                  if (isAccessible) {
                    onStepChange(step.id);
                  }
                }}
              >
                <div className="flex items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${STEP_COLOR_CLASSES[step.color].bg500
                    } ${isActive ? 'shadow-lg ring-2 ring-white' : ''
                    }`}>
                    {isCompleted && !isActive ? (
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    ) : (
                      <Icon className="w-6 h-6 text-white" />
                    )}
                  </div>

                  <div className="flex-grow min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className={`font-semibold text-base ${STEP_COLOR_CLASSES[step.color].text700
                        }`}>
                        {step.title}
                      </h3>
                      {isActive && (
                        <PlayCircle className={`w-5 h-5 ${STEP_COLOR_CLASSES[step.color].text500} flex-shrink-0 ml-2`} />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{step.description}</p>

                    {/* Barre de progression pour mobile */}
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${STEP_COLOR_CLASSES[step.color].bg500
                            }`}
                          style={{
                            width: isCompleted ? '100%' : isActive ? '50%' : '0%',
                            opacity: isCompleted ? 1 : isActive ? 0.8 : 0
                          }}
                        />
                      </div>
                      {isCompleted && (
                        <span className={`text-xs font-medium ${STEP_COLOR_CLASSES[step.color].text700}`}>Terminé</span>
                      )}
                      {isActive && !isCompleted && (
                        <span className="text-xs text-gray-500 font-medium">En cours</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

JourneyPresentation.displayName = 'JourneyPresentation';

// ============================================================================
// COMPOSANT PRINCIPAL - VERSION MOBILE OPTIMISÉE AVEC ASSISTANCE CONTEXTUELLE
// ============================================================================

const AladinnHomePage = React.memo(({ userId, currentCompetence, onSelectCompetence }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { objectifId } = useParams(); // NOUVEAU : Récupérer l'objectifId depuis l'URL

  // États principaux - navigation et UI
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false); // NOUVEAU : État pour collapse desktop
  const [currentStep, setCurrentStep] = useState('ambition');
  const [userObjectives, setUserObjectives] = useState([]);
  const [loadingObjectives, setLoadingObjectives] = useState(false);
  const [hasCompetence, setHasCompetence] = useState(!!currentCompetence);
  const [isMobile, setIsMobile] = useState(false);
  const [inspirationModuleKey, setInspirationModuleKey] = useState(0); // NOUVEAU : Key pour forcer le remontage

  // États pour l'assistance contextuelle
  const [showGuidedTour, setShowGuidedTour] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);

  // NOUVEAU : Charger automatiquement l'objectif si objectifId dans l'URL
  useEffect(() => {
    if (!objectifId || !userId) return;

    const loadObjectifFromUrl = async () => {
      try {
        console.log('🔍 Chargement objectif depuis URL:', objectifId);

        const objectifRef = doc(db, 'users', userId, 'competences', objectifId);
        const objectifSnap = await getDoc(objectifRef);

        if (objectifSnap.exists()) {
          const objectifData = {
            id: objectifSnap.id,
            ...objectifSnap.data()
          };

          console.log('✅ Objectif chargé:', objectifData.title || objectifData.objectifTitle);

          // Sélectionner cet objectif
          if (onSelectCompetence) {
            onSelectCompetence(objectifData);
          }

          // Persister comme objectif courant + marquer dernier accès
          try {
            if (userId) {
              await userPreferencesManager.setCurrentObjective(userId, {
                id: objectifData.id,
                title: objectifData.title || objectifData.objectifTitle || '',
                theme: objectifData.theme || objectifData.domain || objectifData.chapitre || null,
                chapitre: objectifData.chapitre || null
              });
              await userPreferencesManager.updateLastAccessed(userId);
            }
          } catch (e) {
            console.error('AladinnHomePage: erreur de persistance objectif courant:', e);
          }

          // Déterminer l'étape selon le paramètre ?step= ou le contenu
          const searchParams = new URLSearchParams(location.search);
          const stepParam = searchParams.get('step');

          if (stepParam && ['ambition', 'strategy', 'action', 'inspiration', 'exploration'].includes(stepParam)) {
            console.log('📍 Navigation vers étape:', stepParam);
            setCurrentStep(stepParam);
          } else if (objectifData.actionPlan && objectifData.actionPlan.steps && objectifData.actionPlan.steps.length > 0) {
            setCurrentStep('action');
          } else if (objectifData.recommendation) {
            setCurrentStep('strategy');
          } else {
            setCurrentStep('ambition');
          }

          // Nettoyer l'URL (enlever l'objectifId, garder ?step= si présent)
          const newUrl = stepParam ? `/HomePage?step=${stepParam}` : '/HomePage';
          navigate(newUrl, { replace: true });
        } else {
          console.warn('⚠️ Objectif non trouvé:', objectifId);
        }
      } catch (error) {
        console.error('❌ Erreur chargement objectif:', error);
      }
    };

    loadObjectifFromUrl();
  }, [objectifId, userId, onSelectCompetence, location.search, navigate]);

  // Détection des nouveaux utilisateurs
  useEffect(() => {
    const checkFirstTimeUser = () => {
      const hasSeenWelcome = localStorage.getItem('aladinn_welcome_seen');
      const hasObjectives = userObjectives.length > 0;
      const forceShowGuide = location.state?.showGuide;

      if (forceShowGuide || (!hasSeenWelcome && !hasObjectives && !currentCompetence)) {
        setIsFirstTimeUser(true);
        setShowGuidedTour(true);
      }
    };

    // Attendre que les objectifs soient chargés avant de vérifier
    if (userObjectives !== undefined) {
      checkFirstTimeUser();
    }
  }, [userObjectives, currentCompetence, location.state]);

  // Fonction pour ouvrir le guide contextuel
  const handleShowGuide = useCallback(() => {
    setShowGuidedTour(true);
  }, []);

  // Fonction pour fermer le guide contextuel
  const handleCloseGuide = useCallback(() => {
    setShowGuidedTour(false);
    setIsFirstTimeUser(false);
  }, []);

  // Détecter si on est sur mobile et charger la préférence de collapse
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      // Fermer la sidebar automatiquement sur mobile
      if (window.innerWidth < 1024) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    // Charger la préférence de collapse depuis localStorage
    const savedCollapsedState = localStorage.getItem('aladinn_sidebar_collapsed');
    if (savedCollapsedState !== null) {
      setIsSidebarCollapsed(savedCollapsedState === 'true');
    }

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Configuration du parcours
  const journeySteps = useMemo(() => [
    {
      id: 'ambition',
      title: 'Ambition',
      description: 'Formuler votre objectif',
      icon: Lightbulb,
      color: 'purple',
      completed: hasCompetence,
      route: '/formulation-objectif'
    },
    {
      id: 'strategy',
      title: 'Stratégie',
      description: 'Recommandations personnalisées',
      icon: Compass,
      color: 'blue',
      completed: hasCompetence && currentCompetence?.recommendation,
      route: '/Recommandations'
    },
    {
      id: 'action',
      title: 'Plan d\'action',
      description: 'Actions concrètes',
      icon: Zap,
      color: 'orange',
      completed: hasCompetence && currentCompetence?.actionPlan,
      route: '/actionPlan'
    },
    {
      id: 'inspiration',
      title: 'Inspiration',
      description: 'Recherche 360°',
      icon: Search,
      color: 'green',
      completed: hasCompetence && (
        currentCompetence?.webSources?.length > 0 ||
        currentCompetence?.lastInspirationSynthesis ||
        currentCompetence?.inspirationUpdatedAt
      ),
      route: '/webSource'
    },
    {
      id: 'exploration',
      title: 'Exploration',
      description: 'Base de connaissances',
      icon: GraduationCap,
      color: 'teal',
      completed: hasCompetence && (
        currentCompetence?.knowledgeBase ||
        currentCompetence?.documentsCount > 0 ||
        currentCompetence?.lastExplorationActivity
      ),
      route: null
    }
  ], [hasCompetence, currentCompetence]);

  // Charger les objectifs de l'utilisateur
  useEffect(() => {
    const fetchUserObjectives = async () => {
      if (!userId) return;

      setLoadingObjectives(true);
      try {
        const competencesRef = collection(db, 'users', userId, 'competences');
        const q = query(competencesRef, orderBy('updatedAt', 'desc'));
        const querySnapshot = await getDocs(q);

        const objectives = [];
        querySnapshot.forEach((doc) => {
          objectives.push({ id: doc.id, ...doc.data() });
        });

        setUserObjectives(objectives);
      } catch (error) {
        console.error('Erreur lors du chargement des objectifs:', error);
      } finally {
        setLoadingObjectives(false);
      }
    };

    fetchUserObjectives();
  }, [userId]);

  // Mettre à jour l'état quand currentCompetence change
  useEffect(() => {
    setHasCompetence(!!currentCompetence);
  }, [currentCompetence]);

  // Fermer la sidebar quand on clique en dehors (mobile)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && isSidebarOpen && !event.target.closest('.sidebar-container') && !event.target.closest('.sidebar-toggle')) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, isSidebarOpen]);

  // Empêcher le scroll du body quand la sidebar mobile est ouverte
  useEffect(() => {
    if (isMobile && isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobile, isSidebarOpen]);

  useEffect(() => {
    // Gérer la navigation depuis d'autres pages avec state
    const locationState = location?.state;
    if (locationState?.targetStep) {
      setCurrentStep(locationState.targetStep);

      // Réinitialiser l'objectif courant si demandé
      if (locationState.clearCurrentObjective && onSelectCompetence) {
        onSelectCompetence(null);
      }

      // Nettoyer le state pour éviter la répétition
      if (locationState.fromObjectivesList) {
        navigate('/HomePage', { replace: true, state: null });
      }
    }
  }, [location?.state, navigate, onSelectCompetence]);

  // Handlers de navigation
  const handleStepChange = useCallback((stepId) => {
    setCurrentStep(stepId);
  }, []);

  const handleNavigateToStep = useCallback((stepId) => {
    setCurrentStep(stepId);
  }, []);

  const handleCloseSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  // NOUVEAU : Handler pour toggle collapse (desktop)
  const handleToggleCollapse = useCallback(() => {
    const newCollapsedState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newCollapsedState);
    // Sauvegarder la préférence
    localStorage.setItem('aladinn_sidebar_collapsed', newCollapsedState.toString());
  }, [isSidebarCollapsed]);

  // Wrapper pour onSelectCompetence - remonte UNIQUEMENT si changement de compétence (ID différent)
  const handleSelectCompetence = useCallback((competence) => {
    // Vérifier si c'est un vrai changement de compétence (ID différent)
    const isCompetenceChange = competence?.id !== currentCompetence?.id;

    if (isCompetenceChange) {
      console.log('🔄 Changement de compétence détecté, remontage d\'InspirationModule');
      // Incrémenter la key pour forcer le remontage
      setInspirationModuleKey(prev => prev + 1);
    } else {
      console.log('📝 Mise à jour de l\'objectif (même compétence), pas de remontage');
    }

    // Appeler le callback original dans tous les cas
    if (onSelectCompetence) {
      onSelectCompetence(competence);
    }
  }, [onSelectCompetence, currentCompetence?.id]);

  // Props pour les modules
  const moduleProps = {
    userId,
    currentCompetence,
    onSelectCompetence: handleSelectCompetence,
    userObjectives,
    onNavigateToStep: handleNavigateToStep,
    navigate
  };

  // Rendu du contenu selon l'étape
  const renderStepContent = useCallback(() => {
    const step = journeySteps.find(s => s.id === currentStep) || journeySteps[0];

    if (step.id === 'ambition') {
      return <ObjectiveModule {...moduleProps} step={step} />;
    }

    if (step.id === 'strategy' || step.id === 'action') {
      return (
        <StrategyActionModule
          {...moduleProps}
          step={step}
          currentStep={currentStep}
          onStepChange={handleStepChange}
        />
      );
    }

    if (step.id === 'inspiration') {
      return <InspirationModule key={inspirationModuleKey} {...moduleProps} step={step} />;
    }

    if (step.id === 'exploration') {
      return <ExplorationModule {...moduleProps} step={step} />;
    }

    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 lg:p-8 text-center">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">
          Étape en construction
        </h3>
        <p className="text-gray-600">
          Cette étape du parcours est en cours de développement.
        </p>
      </div>
    );
  }, [currentStep, journeySteps, moduleProps, handleStepChange]);

  return (
    <div className="min-h-screen bg-gray-50 flex relative overflow-x-hidden">
      {/* Overlay pour mobile */}
      {isMobile && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={handleCloseSidebar}
        />
      )}

      {/* Barre latérale parcours - VERSION RESPONSIVE AVEC COLLAPSE */}
      <div className={`
        sidebar-container
        ${isMobile ? 'fixed' : 'relative'} 
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} 
        ${isMobile
          ? 'w-72 max-w-[80vw]'
          : isSidebarCollapsed
            ? 'w-16'
            : 'w-full max-w-[280px] lg:max-w-[320px] xl:w-64'
        } 
        bg-white border-r border-gray-200 transition-all duration-300 ease-in-out
        ${isMobile ? 'z-50 h-full' : 'z-auto'}
        ${!isSidebarOpen && !isMobile ? 'lg:w-0 lg:overflow-hidden' : ''}
      `}>
        <JourneyNavigation
          currentStep={currentStep}
          onStepChange={handleStepChange}
          currentCompetence={currentCompetence}
          journeySteps={journeySteps}
          onCreateObjective={() => handleNavigateToStep('ambition')}
          onListObjectives={() => handleNavigateToStep('ambition')}
          userObjectives={userObjectives}
          navigate={navigate}
          isMobile={isMobile}
          onClose={handleCloseSidebar}
          onShowGuide={handleShowGuide}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={handleToggleCollapse}
        />
      </div>

      {/* Zone principale - VERSION CORRIGÉE */}
      <div className="flex-1 flex flex-col min-w-0 w-full overflow-x-hidden">
        {/* Header mobile avec bouton menu parcours à droite */}
        {isMobile && (
          <div className="bg-white border-b border-gray-200 px-3 py-2 flex justify-end items-center">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="sidebar-toggle p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Menu Parcours"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Contenu principal */}
        <div className="flex-1 overflow-y-auto p-3 lg:p-6">
          <div className="max-w-4xl mx-auto w-full space-y-6">
            {/* Bandeau d'aide pour nouveaux utilisateurs */}
            {isFirstTimeUser && !showGuidedTour && (
              <WelcomeBanner
                onClose={() => setIsFirstTimeUser(false)}
                onShowGuide={handleShowGuide}
              />
            )}

            {/* Contenu de l'étape sélectionnée uniquement */}
            {renderStepContent()}

            {/* Section informative mobile - sans aide contextuelle */}
            {isMobile && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-200">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Star className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-purple-800 mb-1">Astuce Aladinn</h3>
                    <p className="text-sm text-purple-700">
                      Utilisez la navigation en bas d'écran pour accéder rapidement aux différents espaces d'Aladinn.
                      Le bouton "Parcours" vous ramène toujours à cette vue guidée.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tour guidé pour nouveaux utilisateurs */}
      <GuidedTour
        isOpen={showGuidedTour}
        onClose={handleCloseGuide}
      />

      {/* Bouton d'aide flottant (desktop seulement) */}
      {!isMobile && !showGuidedTour && !isFirstTimeUser && (
        <div className="fixed bottom-6 right-6 z-40">
          <HelpButton
            stepId={currentStep}
            size="lg"
            className="w-14 h-14 shadow-lg hover:scale-110 transition-transform duration-300"
            title="Aide contextuelle"
          />
        </div>
      )}
    </div>
  );
});

AladinnHomePage.displayName = 'AladinnHomePage';

export default AladinnHomePage;
